import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { SECTORS, SECTOR_BY_SLUG } from "@/lib/sectors";
import { api, formatApiError } from "@/lib/api";
import { CLAIM } from "@/constants/testIds";
import Seo from "@/components/Seo";

export default function ClaimForm() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const initialSlug = params.get("sector");

    const [sectorSlug, setSectorSlug] = useState(initialSlug && SECTOR_BY_SLUG[initialSlug] ? initialSlug : "");
    const [step, setStep] = useState(initialSlug ? 1 : 0);
    const [answers, setAnswers] = useState({});
    const [personal, setPersonal] = useState({ full_name: "", email: "", phone: "", postcode: "", details_text: "" });
    const [consent, setConsent] = useState({ gdpr: false, marketing: false });
    const [submitting, setSubmitting] = useState(false);

    const wizardRef = useRef(null);
    const prevStepRef = useRef(step);

    const sector = sectorSlug ? SECTOR_BY_SLUG[sectorSlug] : null;
    const totalSteps = sector ? 1 + sector.questions.length + 1 : 1;
    const progress = sector ? Math.round(((step + 1) / (totalSteps + 1)) * 100) : 5;

    useEffect(() => {
        // when sector changes, reset answers
        setAnswers({});
    }, [sectorSlug]);

    // Keep the wizard anchored to the top of the viewport on every step CHANGE so the
    // current question is always perfectly positioned — critical for mobile conversion.
    // Guard with prevStep ref instead of a "first mount" boolean so React.StrictMode's
    // double-invoke in dev doesn't trigger an unwanted scroll on initial load.
    useEffect(() => {
        if (prevStepRef.current === step) return;
        prevStepRef.current = step;

        // Blur any focused input so the mobile keyboard closes before we scroll.
        if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }

        const node = wizardRef.current;
        if (!node) return;

        // Double rAF ensures the new step's layout is fully committed before measuring.
        // `scrollIntoView` then relies on the wizard's scroll-mt-* CSS for the offset.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                node.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        });
    }, [step]);

    const canNext = useMemo(() => {
        if (step === 0) return !!sectorSlug;
        if (!sector) return false;
        if (step >= 1 && step <= sector.questions.length) {
            const q = sector.questions[step - 1];
            const v = answers[q.key];
            if (q.type === "checkboxes") return Array.isArray(v) && v.length > 0;
            return v != null && String(v).trim().length > 0;
        }
        // Final step (personal + consent)
        return (
            personal.full_name.trim().length >= 2 &&
            /\S+@\S+\.\S+/.test(personal.email) &&
            personal.phone.trim().length >= 6 &&
            personal.postcode.trim().length >= 2 &&
            consent.gdpr
        );
    }, [step, sectorSlug, sector, answers, personal, consent]);

    function handleNext() {
        if (!canNext) return;
        if (step < totalSteps - 1) {
            setStep((s) => s + 1);
        } else {
            handleSubmit();
        }
    }

    async function handleSubmit() {
        if (submitting || !sector) return;
        setSubmitting(true);
        try {
            const payload = {
                sector_slug: sector.slug,
                sector_name: sector.name,
                full_name: personal.full_name.trim(),
                email: personal.email.trim().toLowerCase(),
                phone: personal.phone.trim(),
                postcode: personal.postcode.trim().toUpperCase(),
                details: { ...answers, free_text: personal.details_text || undefined },
                gdpr_consent: consent.gdpr,
                marketing_consent: consent.marketing,
            };
            const { data } = await api.post("/enquiries", payload);
            navigate(`/thank-you?ref=${encodeURIComponent(data.reference)}&sector=${encodeURIComponent(sector.slug)}`);
        } catch (err) {
            toast.error(formatApiError(err));
            setSubmitting(false);
        }
    }

    return (
        <section className="bg-slate-50 min-h-[calc(100vh-5rem)]">
            <Seo
                title={sector ? `Start your ${sector.name} claim` : "Start your claim"}
                description="Free 60-second eligibility check. No upfront fees, No Win No Fee. A specialist reviews your enquiry within one working day."
                path="/claim"
            />
            <div className="container-x py-10 lg:py-16">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700 mb-8">
                    <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>

                <div className="grid lg:grid-cols-12 gap-10">
                    <aside className="lg:col-span-4">
                        <div className="lg:sticky lg:top-28">
                            <span className="text-eyebrow">Free claim check</span>
                            <h1 className="mt-3 text-3xl sm:text-4xl font-display font-bold tracking-tight text-navy-900">
                                Let&apos;s see what you&apos;re owed.
                            </h1>
                            <p className="mt-4 text-base text-slate-600 leading-relaxed">
                                Around 60 seconds. No upfront cost. No obligation to proceed. Submitted enquiries are reviewed by a specialist within one working day.
                            </p>

                            <div className="mt-8 space-y-3">
                                {[
                                    { icon: ShieldCheck, label: "FCA aware firm" },
                                    { icon: Lock, label: "Bank-grade encryption" },
                                    { icon: CheckCircle2, label: "100% No Win, No Fee" },
                                ].map((it) => (
                                    <div key={it.label} className="flex items-center gap-3 text-sm text-slate-700">
                                        <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
                                            <it.icon className="w-4 h-4 text-emerald-600" />
                                        </div>
                                        {it.label}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <div className="lg:col-span-8">
                        <div ref={wizardRef} data-testid={CLAIM.wizard} className="scroll-mt-20 lg:scroll-mt-28 bg-white rounded-3xl border border-slate-200 shadow-card-soft p-6 sm:p-10">
                            <div className="flex items-center justify-between mb-3 text-xs font-semibold text-slate-500">
                                <span>Step {step + 1} of {totalSteps}</span>
                                <span>{progress}%</span>
                            </div>
                            <Progress data-testid={CLAIM.progressBar} value={progress} className="h-2 bg-slate-100 [&>div]:bg-emerald-500" />

                            <div className="mt-8">
                                {step === 0 && (
                                    <SectorPicker
                                        value={sectorSlug}
                                        onChange={(v) => { setSectorSlug(v); }}
                                    />
                                )}
                                {step > 0 && step <= (sector?.questions.length || 0) && (
                                    <QuestionStep
                                        question={sector.questions[step - 1]}
                                        value={answers[sector.questions[step - 1].key]}
                                        onChange={(v) => setAnswers((a) => ({ ...a, [sector.questions[step - 1].key]: v }))}
                                    />
                                )}
                                {step === (sector?.questions.length || 0) + 1 && sector && (
                                    <PersonalStep
                                        sectorName={sector.name}
                                        personal={personal}
                                        setPersonal={setPersonal}
                                        consent={consent}
                                        setConsent={setConsent}
                                    />
                                )}
                            </div>

                            <div className="mt-10 flex items-center justify-between pt-6 border-t border-slate-100">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    data-testid={CLAIM.backButton}
                                    disabled={step === 0 || submitting}
                                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                                    className="text-slate-600"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
                                </Button>

                                <button
                                    type="button"
                                    data-testid={step === totalSteps - 1 ? CLAIM.submitButton : CLAIM.nextButton}
                                    disabled={!canNext || submitting}
                                    onClick={handleNext}
                                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? (
                                        <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                                    ) : step === totalSteps - 1 ? (
                                        <>Submit my claim <ArrowRight className="w-4 h-4" /></>
                                    ) : (
                                        <>Continue <ArrowRight className="w-4 h-4" /></>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SectorPicker({ value, onChange }) {
    return (
        <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-navy-900">What kind of claim?</h2>
            <p className="mt-2 text-slate-600">Choose the category that fits best. You can change this later.</p>

            <div data-testid={CLAIM.sectorSelect} className="mt-7 grid sm:grid-cols-2 gap-3">
                {SECTORS.map((s) => {
                    const Icon = Icons[s.icon] || Icons.Scale;
                    const active = value === s.slug;
                    return (
                        <button
                            type="button"
                            key={s.slug}
                            onClick={() => onChange(s.slug)}
                            className={`text-left p-4 rounded-2xl border transition-all ${active ? "border-emerald-500 bg-emerald-50 shadow-glow-emerald" : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30"}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${active ? "bg-emerald-600" : "bg-emerald-50 border border-emerald-100"}`}>
                                    <Icon className={`w-5 h-5 ${active ? "text-white" : "text-emerald-600"}`} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-semibold text-navy-900 text-sm">{s.name}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{s.short}</div>
                                </div>
                                {active && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function QuestionStep({ question, value, onChange }) {
    if (question.type === "select") {
        return (
            <div>
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-navy-900">{question.label}</h2>
                <p className="mt-2 text-slate-500 text-sm">Pick the best match.</p>
                <div className="mt-7 grid gap-2.5">
                    {question.options.map((opt) => {
                        const active = value === opt;
                        return (
                            <button
                                type="button"
                                key={opt}
                                data-testid={`${CLAIM.field(question.key)}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => onChange(opt)}
                                className={`flex items-center justify-between text-left p-4 rounded-xl border transition-all ${active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}
                            >
                                <span className="text-base font-medium text-navy-900">{opt}</span>
                                {active && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    if (question.type === "checkboxes") {
        const arr = Array.isArray(value) ? value : [];
        return (
            <div>
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-navy-900">{question.label}</h2>
                <p className="mt-2 text-slate-500 text-sm">Select all that apply.</p>
                <div className="mt-7 grid sm:grid-cols-2 gap-2.5">
                    {question.options.map((opt) => {
                        const active = arr.includes(opt);
                        return (
                            <button
                                type="button"
                                key={opt}
                                data-testid={`${CLAIM.field(question.key)}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => onChange(active ? arr.filter((x) => x !== opt) : [...arr, opt])}
                                className={`flex items-center gap-3 text-left p-4 rounded-xl border transition-all ${active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}
                            >
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${active ? "bg-emerald-600 border-emerald-600" : "border-slate-300"}`}>
                                    {active && <CheckCircle2 className="w-4 h-4 text-white" />}
                                </div>
                                <span className="text-sm font-medium text-navy-900">{opt}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }
    return (
        <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-navy-900">{question.label}</h2>
            <p className="mt-2 text-slate-500 text-sm">{question.placeholder || "Type your answer below."}</p>
            <Input
                data-testid={CLAIM.field(question.key)}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={question.placeholder}
                className="mt-7 h-14 text-base border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
            />
        </div>
    );
}

function PersonalStep({ sectorName, personal, setPersonal, consent, setConsent }) {
    return (
        <div>
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-navy-900">Almost done — your details</h2>
            <p className="mt-2 text-slate-500 text-sm">A specialist will review your <strong className="text-navy-900">{sectorName}</strong> enquiry within one working day.</p>

            <div className="mt-7 grid sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-sm font-semibold text-navy-900">Full name</Label>
                    <Input
                        data-testid={CLAIM.fullName}
                        value={personal.full_name}
                        onChange={(e) => setPersonal((p) => ({ ...p, full_name: e.target.value }))}
                        placeholder="e.g. Alex Morgan"
                        className="mt-2 h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                    />
                </div>
                <div>
                    <Label className="text-sm font-semibold text-navy-900">Email</Label>
                    <Input
                        data-testid={CLAIM.email}
                        type="email"
                        value={personal.email}
                        onChange={(e) => setPersonal((p) => ({ ...p, email: e.target.value }))}
                        placeholder="you@example.co.uk"
                        className="mt-2 h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                    />
                </div>
                <div>
                    <Label className="text-sm font-semibold text-navy-900">Phone</Label>
                    <Input
                        data-testid={CLAIM.phone}
                        type="tel"
                        value={personal.phone}
                        onChange={(e) => setPersonal((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="07XXX XXXXXX"
                        className="mt-2 h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                    />
                </div>
                <div>
                    <Label className="text-sm font-semibold text-navy-900">Postcode</Label>
                    <Input
                        data-testid={CLAIM.postcode}
                        value={personal.postcode}
                        onChange={(e) => setPersonal((p) => ({ ...p, postcode: e.target.value }))}
                        placeholder="e.g. SW1A 1AA"
                        className="mt-2 h-12 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                    />
                </div>
                <div className="sm:col-span-2">
                    <Label className="text-sm font-semibold text-navy-900">Anything else we should know? <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Textarea
                        value={personal.details_text}
                        onChange={(e) => setPersonal((p) => ({ ...p, details_text: e.target.value }))}
                        placeholder="A few sentences about your situation — totally optional, but helpful."
                        className="mt-2 min-h-[110px] border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 rounded-xl"
                    />
                </div>
            </div>

            <div className="mt-7 space-y-3">
                <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors">
                    <Checkbox
                        data-testid={CLAIM.gdprConsent}
                        checked={consent.gdpr}
                        onCheckedChange={(v) => setConsent((c) => ({ ...c, gdpr: !!v }))}
                        className="mt-0.5 border-slate-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    />
                    <div className="text-sm text-slate-700 leading-relaxed">
                        <span className="font-semibold text-navy-900">I agree</span> to ClaimHub UK reviewing my enquiry and processing my personal data in line with the <Link to="/legal/privacy" className="text-emerald-700 underline">Privacy Policy</Link>. Required to proceed.
                    </div>
                </label>
                <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-emerald-300 transition-colors">
                    <Checkbox
                        data-testid={CLAIM.marketingConsent}
                        checked={consent.marketing}
                        onCheckedChange={(v) => setConsent((c) => ({ ...c, marketing: !!v }))}
                        className="mt-0.5 border-slate-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    />
                    <div className="text-sm text-slate-700 leading-relaxed">
                        Optionally, send me updates about other claim categories I might qualify for. You can opt out any time.
                    </div>
                </label>
            </div>
        </div>
    );
}
